/**
 * EditorObjectList: DOM list of every object in the current scene.
 *
 * Click selects (synced with EditorState); right-click deletes. Re-renders on
 * any state change (selection, add/delete, marker edits). The list reads from
 * the live `SceneObjectManager` so it always reflects what's in the world.
 */

import type { EditorState } from './EditorState';
import type { SceneObjectManager } from '../scene/SceneObjectManager';

export interface ObjectListCallbacks {
    select(id: string | null): void;
    delete(id: string): void;
}

export class EditorObjectList {
    private _mount: HTMLElement;
    private _state: EditorState;
    private _getMgr: () => SceneObjectManager | null;
    private _cb: ObjectListCallbacks;
    private _unsubscribe: () => void;

    constructor(
        mount: HTMLElement,
        state: EditorState,
        getMgr: () => SceneObjectManager | null,
        cb: ObjectListCallbacks
    ) {
        this._mount = mount;
        this._state = state;
        this._getMgr = getMgr;
        this._cb = cb;
        this._unsubscribe = state.subscribe(() => this._render());
        this._render();
    }

    private _render(): void {
        const mgr = this._getMgr();
        const objects = mgr ? mgr.objects : [];
        const sel = this._state.selectedId;
        this._mount.innerHTML = '';

        if (!objects.length) {
            const empty = document.createElement('div');
            empty.className = 'ep-empty';
            empty.textContent = '场景暂无对象';
            this._mount.appendChild(empty);
            return;
        }

        for (const o of objects) {
            const row = document.createElement('div');
            row.className = 'ol-row' + (o.id === sel ? ' selected' : '');
            row.title = `${o.type} · ${o.id}`;
            const typeTag = document.createElement('span');
            typeTag.className = 'ol-type';
            typeTag.textContent = o.type;
            const name = document.createElement('span');
            name.className = 'ol-name';
            name.textContent = o.name;
            const del = document.createElement('button');
            del.className = 'ol-del';
            del.textContent = '×';
            del.title = '删除';
            del.addEventListener('click', (e) => {
                e.stopPropagation();
                this._cb.delete(o.id);
            });
            row.appendChild(typeTag);
            row.appendChild(name);
            row.appendChild(del);

            row.addEventListener('click', () => this._cb.select(o.id));

            this._mount.appendChild(row);
        }
    }

    destroy(): void { this._unsubscribe(); this._mount.innerHTML = ''; }
}
